/**
 * Origin Cell Renderer
 *
 * Renders the vulnerability origin(s) as chips.
 * Displays the tool(s) or integration(s) that discovered the vulnerability.
 */

import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import type { RiskWithVulnerability } from '@/hooks/useRisks';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { getDisplayOriginTexts } from '@/utils/originDisplay.util';

interface OriginCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for vulnerability origin column
 *
 * Displays one or more origins as brand-variant chips with Chariot rollup logic.
 * Origins are transformed by getDisplayOriginTexts before rendering:
 * - Native Chariot capabilities (subdomain-enumerator, portscanner, etc.) → "Chariot"
 * - Third-party integrations (github, aws, qualys, etc.) → Formatted integration name
 * - Manual imports (emails ending in @praetorian.com) → "Import"
 * - Multiple origins are deduplicated (e.g., two native sources → one "Chariot" chip)
 *
 * Features:
 * - Shows all origins without truncation
 * - Uses brand variant for consistent styling
 * - Handles empty origins gracefully
 * - Backward compatible with legacy risk.source field
 *
 * @param risk - The vulnerability/risk with origins array or legacy source field
 * @returns ChipsList component with origin chips or dash if no origins
 */
export const OriginCell: React.FC<OriginCellProps> = React.memo(({ risk }) => {
  // Get source filter options for Chariot group detection
  const { sortedOptions: sourceFilterOptions } = useEntityFilters(
    'Vulnerability',
    'vuln-table'
  );

  // Use origins array if available, fallback to source for backward compatibility
  const rawOrigins = risk.origins?.length
    ? risk.origins
    : risk.source
      ? [risk.source]
      : [];

  // Transform origins using the same logic as Assets table
  const displayOrigins = useMemo(
    () => getDisplayOriginTexts(rawOrigins, sourceFilterOptions),
    [rawOrigins, sourceFilterOptions]
  );

  if (!displayOrigins.length) return <>-</>;

  return (
    <ChipsList
      items={displayOrigins}
      maxVisible={1} // Show all origins without truncation
      variant="brand"
      className="max-w-[140px] min-w-[140px]"
      size="xs"
      data-testid="origin-chips"
    />
  );
});

OriginCell.displayName = 'OriginCell';
